import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
  alpha,
  Paper,
  Stack,
  Divider
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { keyframes } from "@emotion/react";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EmailIcon from "@mui/icons-material/Email";
import SearchIcon from "@mui/icons-material/Search";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";

// Animations
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const rotateAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const bounceAnimation = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const pulseAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const NotFound = () => {
  const theme = useTheme();
  const location = useLocation();
  const currentPath = location.pathname;

  const suggestions = [
    { text: "Go to Homepage", path: "/", icon: <HomeIcon /> },
    { text: "Check our Pricing", path: "/pricing", icon: <CleaningServicesIcon /> },
    { text: "Browse Features", path: "/features", icon: <SearchIcon /> },
    { text: "Contact Support", path: "/contact", icon: <EmailIcon /> },
    { text: "Visit Help Center", path: "/help", icon: <HelpOutlineIcon /> },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.primary.dark, 0.01)} 100%)`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Shapes */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          left: "-5%",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
          animation: `${floatAnimation} 8s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "10%",
          right: "-5%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.08)} 0%, transparent 70%)`,
          animation: `${floatAnimation} 10s ease-in-out infinite reverse`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "600px",
          height: "600px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.03)} 0%, transparent 70%)`,
          animation: `${rotateAnimation} 30s linear infinite`,
        }}
      />

      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          {/* Left Column - Error Message */}
          <Grid item xs={12} md={6}>
            <Box>
              {/* Animated 404 Text */}
              <Box sx={{ mb: 3, position: "relative" }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "6rem", md: "8rem", lg: "10rem" },
                    fontWeight: 900,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                    letterSpacing: "0.05em",
                    animation: `${pulseAnimation} 2s ease-in-out infinite`,
                  }}
                >
                  404
                </Typography>
                <SentimentVeryDissatisfiedIcon
                  sx={{
                    position: "absolute",
                    right: { xs: 20, md: 40 },
                    top: { xs: 10, md: 20 },
                    fontSize: { xs: 40, md: 60 },
                    color: alpha(theme.palette.warning.main, 0.3),
                    animation: `${bounceAnimation} 2s ease-in-out infinite`,
                  }}
                />
              </Box>

              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.8rem", md: "2.5rem" },
                  fontWeight: 800,
                  mb: 2,
                  color: theme.palette.text.primary,
                }}
              >
                Oops! Page Not Found
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  color: theme.palette.text.secondary,
                  mb: 4,
                  lineHeight: 1.6,
                }}
              >
                The page you're looking for doesn't exist or has been moved.
                Let's get you back on track.
              </Typography>

              {/* Path Highlight */}
              {currentPath !== "/404" && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 4,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.error.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
                    display: "inline-block",
                  }}
                >
                  <Typography variant="body2" color="error.main" fontFamily="monospace">
                    {currentPath}
                  </Typography>
                </Paper>
              )}

              {/* Action Buttons */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 4 }}>
                <Button
                  component={Link}
                  to="/"
                  variant="contained"
                  size="large"
                  startIcon={<HomeIcon />}
                  sx={{
                    borderRadius: "50px",
                    px: 4,
                    py: 1.2,
                    fontSize: "1rem",
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Back to Home
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => window.history.back()}
                  sx={{
                    borderRadius: "50px",
                    px: 4,
                    py: 1.2,
                    fontSize: "1rem",
                    fontWeight: 600,
                    borderColor: theme.palette.divider,
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Go Back
                </Button>
              </Stack>

              {/* Help Text */}
              <Typography variant="body2" color="text.secondary">
                Need help? <Link to="/contact" style={{ color: theme.palette.primary.main, textDecoration: "none", fontWeight: 600 }}>Contact our support team</Link>
              </Typography>
            </Box>
          </Grid>

          {/* Right Column - Suggestions & Illustration */}
          <Grid item xs={12} md={6}>
            <Box>
              {/* Animated Illustration */}
              <Box
                sx={{
                  position: "relative",
                  mb: 4,
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: { xs: 200, md: 300 },
                    height: { xs: 200, md: 300 },
                    mx: "auto",
                    position: "relative",
                    animation: `${floatAnimation} 6s ease-in-out infinite`,
                  }}
                >
                  <CleaningServicesIcon
                    sx={{
                      fontSize: { xs: 80, md: 120 },
                      color: alpha(theme.palette.primary.main, 0.2),
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                  <SearchIcon
                    sx={{
                      fontSize: { xs: 50, md: 80 },
                      color: alpha(theme.palette.warning.main, 0.3),
                      position: "absolute",
                      top: "30%",
                      right: "20%",
                      animation: `${rotateAnimation} 10s linear infinite`,
                    }}
                  />
                </Box>
              </Box>

              {/* Suggestions Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  bgcolor: "white",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  You might be looking for:
                </Typography>

                <Stack spacing={1.5}>
                  {suggestions.map((suggestion, index) => (
                    <Button
                      key={index}
                      component={Link}
                      to={suggestion.path}
                      startIcon={suggestion.icon}
                      sx={{
                        justifyContent: "flex-start",
                        textAlign: "left",
                        p: 1.5,
                        borderRadius: 2,
                        textTransform: "none",
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          transform: "translateX(8px)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      {suggestion.text}
                    </Button>
                  ))}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary">
                    Or search our <Link to="/faqs" style={{ color: theme.palette.primary.main, textDecoration: "none" }}>FAQs</Link> for answers
                  </Typography>
                </Box>
              </Paper>

              {/* Fun Fact */}
              <Box sx={{ mt: 3, textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  💡 Fun fact: We clean over 50,000 properties monthly. This page? Not one of them.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default NotFound;